<template>
  <BottomModal :visible="show" :full-height="true" background-color="#ffffff" @close="handleClose">
    <template #default>
      <div class="success-wrapper">

        <div v-if="!isActivationPending" class="confetti-bg" aria-hidden="true">
          <span v-for="n in 18" :key="n" :class="`confetti-piece confetti-${n}`"></span>
        </div>

        <template v-if="!isActivationPending">
          <div class="success-icon-circle">
            <i class="fa-solid fa-gift"></i>
          </div>

          <div class="success-body">
            <h2 class="success-headline">Evento criado! 🎉</h2>
            <p class="success-sub">
              Sua lista de presentes está no ar.<br>
              Compartilhe com seus convidados!
            </p>
          </div>

          <div v-if="eventShareUrl" class="share-card">
            <p class="share-label">
              <i class="fa-solid fa-link me-1"></i> Link do evento
            </p>
            <div class="share-url-row">
              <span class="share-url-text">{{ shortUrl }}</span>
              <button class="btn-copy" @click="copyLink" :class="{ copied: linkCopied }">
                <i :class="linkCopied ? 'fa-solid fa-check' : 'fa-regular fa-copy'"></i>
                {{ linkCopied ? 'Copiado!' : 'Copiar' }}
              </button>
            </div>
          </div>

          <div class="success-actions">
            <button class="btn-primary-pink" @click="handleGoToEvent">
              <i class="fa-solid fa-pen-to-square me-2"></i>
              Editar meu evento
            </button>
            <button class="btn-ghost btn-customize" @click="handleCustomizeEvent">
              <i class="fa-solid fa-wand-magic-sparkles me-2"></i>
              Personalizar evento
            </button>
            <div class="success-inline-actions">
              <button class="btn-ghost btn-inline" @click="handleGoToInvite">
                <i class="fa-solid fa-envelope-open-text me-1"></i>
                Criar convite
              </button>
              <button v-if="hasRsvp" class="btn-ghost btn-inline" @click="handleGoToGuests">
                <i class="fa-solid fa-users me-1"></i>
                Lista de presença
              </button>
            </div>
            <button v-if="eventShareUrl" class="btn-share-whatsapp" @click="shareWhatsapp">
              <i class="fa-brands fa-whatsapp me-2"></i>
              Compartilhar no WhatsApp
            </button>
          </div>

        </template>

        <template v-else>

          <div class="success-icon-circle warning">
            <i class="fa-solid fa-user-pen"></i>
          </div>

          <div class="success-body success-body--activation">
            <h2 class="success-headline success-headline--activation">Seu evento está pronto!</h2>
            <p class="success-sub success-sub--activation">
              Você já pode compartilhar com seus convidados. Para receber presentes, conclua seu cadastro.
            </p>
          </div>

          <div class="checklist-card">
            <div class="checklist-item pending">
              <span class="checklist-dot"></span>
              <span>Complete seu cadastro para receber presentes</span>
            </div>
          </div>

          <div v-if="eventShareUrl" class="share-card">
            <p class="share-label">
              <i class="fa-solid fa-link me-1"></i> Link do evento
            </p>
            <div class="share-url-row">
              <span class="share-url-text">{{ shortUrl }}</span>
              <button class="btn-copy" @click="copyLink" :class="{ copied: linkCopied }">
                <i :class="linkCopied ? 'fa-solid fa-check' : 'fa-regular fa-copy'"></i>
                {{ linkCopied ? 'Copiado!' : 'Copiar' }}
              </button>
            </div>
          </div>

          <div class="success-actions">
            <button class="btn-primary-pink" @click="goToActivation">
              <i class="fa-solid fa-user-check me-2"></i>
              Concluir cadastro
            </button>
            <button class="btn-ghost" @click="handleGoToEvent">
              <i class="fa-solid fa-eye me-2"></i>
              Ver meu evento
            </button>
          </div>

        </template>

      </div>
    </template>
  </BottomModal>

  <BottomModal
    :visible="showTransfersOnboardingModal"
    :full-height="false"
    background-color="#ffffff"
    @close="closeTransfersOnboardingModal"
  >
    <template #default>
      <div class="p-3">
        <div class="text-center mb-3">
          <div style="font-size: 2rem;">🔐</div>
          <h5 class="fw-bold mt-2 mb-1 text-primary-emphasis">Finalize seus recebimentos</h5>
          <p class="text-muted small mb-0">
            Seu evento foi compartilhado. Para liberar saques, finalize a verificação da conta de recebimento.
          </p>
        </div>

        <div class="onboarding-note mb-3" role="status" aria-live="polite">
          <i class="fas fa-circle-exclamation onboarding-note__icon" aria-hidden="true"></i>
          <p class="onboarding-note__text mb-0">
            {{ onboardingDueFields.length > 0
              ? `Pendências atuais: ${onboardingDueFields.length} item(ns) para completar.`
              : 'Existem pendências de verificação para liberar recebimentos.' }}
          </p>
        </div>

        <p v-if="onboardingSession.clientSecret" class="onboarding-session-hint mb-3">
          Uma sessão de onboarding foi preparada para você. Clique em "Configurar agora" para continuar.
        </p>

        <div class="onboarding-actions">
          <button class="btn btn-outline-secondary w-100" @click="closeTransfersOnboardingModal">
            Agora não
          </button>
          <button class="btn-primary-pink" @click="openTransfersOnboarding">
            Configurar agora
          </button>
        </div>
      </div>
    </template>
  </BottomModal>
</template>

<script>
import BottomModal from '@/components/BottomModal.vue';
import { useAuthStore } from '@/stores/authStore';
import api from '@/services/api';
import { toast } from 'vue3-toastify';

export default {
  name: 'EventSuccessModal',
  components: { BottomModal },

  setup() {
    const authStore = useAuthStore();
    return { authStore };
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    eventId: {
      type: String,
      required: true,
    },
    eventName: {
      type: String,
      default: 'seu evento',
    },
    eventShareUrl: {
      type: String,
      default: '',
    },
    accountBlocked: {
      type: Boolean,
      default: false,
    },
    hasRsvp: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close', 'go-to-event'],

  data() {
    return {
      linkCopied: false,
      hasActivatedTransfers: false,
      showTransfersOnboardingModal: false,
      onboardingDueFields: [],
      onboardingSession: {
        clientSecret: '',
        expiresAt: 0,
      },
    };
  },

  computed: {
    isActivationPending() {
      return this.authStore.isActivationPending;
    },
    shortUrl() {
      if (!this.eventShareUrl) return '';
      const editUrl = `${window.location.origin}/my-account/events/${this.eventId}/edit`;
      return editUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
    },
    whatsappShareUrl() {
      const nome = this.eventName && this.eventName !== 'seu evento' ? ` de ${this.eventName}` : '';
      const msg =
        `🎁 Meu evento${nome} está no ar!\n\n` +
        `Veja minha lista de presentes e ajude a tornar esse momento inesquecível:\n` +
        `${this.eventShareUrl}`;
      return `https://wa.me/?text=${encodeURIComponent(msg)}`;
    },
  },

  methods: {
    handleClose() {
      this.$emit('close');
    },
    handleGoToEvent() {
      window.location.href = `/my-account/events/${this.eventId}/edit`;
    },
    handleGoToInvite() {
      window.location.href = `/my-account/events/${this.eventId}/edit?tab=convite`;
    },
    handleGoToGuests() {
      window.location.href = `/my-account/events/${this.eventId}/edit?tab=convidados`;
    },
    handleCustomizeEvent() {
      window.location.href = `/event/${this.eventId}?customize=1`;
    },
    goToActivation() {
      this.$emit('close');
      this.$router.push('/my-account/profile?ref=createaccount');
    },
    closeTransfersOnboardingModal() {
      this.showTransfersOnboardingModal = false;
    },
    openTransfersOnboarding() {
      this.showTransfersOnboardingModal = false;
      this.authStore.setOnboardingMode(true);
      this.$router.push('/organizer/onboarding/refresh');
    },
    async ensureTransfersActivatedForShare() {
      if (this.hasActivatedTransfers) {
        return;
      }

      try {
        const response = await api.post('/users/finance/connected-accounts/stripe/activate-transfers');
        const payload = response?.data || {};

        if (payload.provider_account_id || payload.provider_account_status) {
          this.authStore.user = {
            ...(this.authStore.user || {}),
            ...(payload.provider_account_id ? { provider_account_id: payload.provider_account_id } : {}),
            ...(payload.provider_account_status ? { provider_account_status: payload.provider_account_status } : {}),
          };

          this.authStore.applyBackendEligibilityFlags(payload);
        }

        if (payload.needs_onboarding) {
          this.onboardingDueFields = Array.isArray(payload.currently_due) ? payload.currently_due : [];
          this.onboardingSession = {
            clientSecret: payload.account_session_client_secret || '',
            expiresAt: payload.account_session_expires_at || 0,
          };
          this.showTransfersOnboardingModal = true;

          toast.info('Você já pode compartilhar seu evento. Para liberar saques, finalize a verificação de recebimentos.', {
            autoClose: 4500,
          });
        }

        this.hasActivatedTransfers = true;
      } catch (error) {
        console.warn('Falha ao ativar transfers no compartilhamento:', error);
        toast.warning('Não foi possível validar seus recebimentos agora. Você ainda pode continuar o compartilhamento.');
      }
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.eventShareUrl);
        this.linkCopied = true;
        setTimeout(() => { this.linkCopied = false; }, 2500);
      } catch {
        const el = document.createElement('textarea');
        el.value = this.eventShareUrl;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        this.linkCopied = true;
        setTimeout(() => { this.linkCopied = false; }, 2500);
      }

      await this.ensureTransfersActivatedForShare();
    },
    async shareWhatsapp() {
      window.open(this.whatsappShareUrl, '_blank', 'noopener,noreferrer');
      await this.ensureTransfersActivatedForShare();
    },
  },
};
</script>

<style scoped>
.success-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.5rem 1.5rem 2rem;
  overflow: hidden;
  min-height: 100%;
  background: #ffffff;
}

.success-icon-circle {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff50a3 0%, #ff8fd1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 24px rgba(255, 80, 163, 0.35);
  animation: pop-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  z-index: 1;
}

.success-icon-circle.warning {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  box-shadow: 0 8px 24px rgba(245, 158, 11, 0.35);
}

.success-icon-circle i {
  font-size: 2.4rem;
  color: #ffffff;
}

@keyframes pop-in {
  0%   { transform: scale(0.4); opacity: 0; }
  100% { transform: scale(1);   opacity: 1; }
}

.success-body {
  z-index: 1;
  margin-bottom: 1.75rem;
}

.success-body--activation {
  width: 100%;
  max-width: 520px;
  margin: 0 auto 1.25rem;
}

.success-headline {
  font-size: 1.65rem;
  font-weight: 700;
  color: #32355d;
  margin-bottom: 0.5rem;
  line-height: 1.25;
}

.success-sub {
  font-size: 0.975rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
}

.success-headline--activation {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.28;
  margin-bottom: 0.45rem;
}

.success-sub--activation {
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
  color: #6b7280;
}

.share-card {
  width: 100%;
  max-width: 380px;
  background: #fdf2f8;
  border: 1.5px solid #fbc8e4;
  border-radius: 1rem;
  padding: 0.9rem 1rem;
  margin-bottom: 1.5rem;
  z-index: 1;
}

.share-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #ff50a3;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.5rem;
}

.share-url-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  border: 1px solid #f0c4dc;
  border-radius: 0.6rem;
  padding: 0.4rem 0.5rem 0.4rem 0.75rem;
}

.share-url-text {
  flex: 1;
  font-size: 0.82rem;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.btn-copy {
  flex-shrink: 0;
  background: #ff50a3;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.3rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  transition: background 0.2s;
  white-space: nowrap;
}

.btn-copy.copied  { background: #16a34a; }
.btn-copy:hover:not(.copied) { background: #e0408e; }

.checklist-card {
  width: 100%;
  max-width: 380px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 0.9rem 1.1rem;
  margin-bottom: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  z-index: 1;
}

.checklist-item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.checklist-item.done  { color: #16a34a; }
.checklist-item.pending { color: #6b7280; }

.checklist-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  flex-shrink: 0;
}

.checklist-check {
  color: #16a34a;
  font-size: 1rem;
  flex-shrink: 0;
}

.success-actions {
  width: 100%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  z-index: 1;
}

.btn-primary-pink {
  width: 100%;
  background: linear-gradient(135deg, #ff50a3, #ff8fd1);
  color: #fff;
  border: none;
  border-radius: 0.85rem;
  padding: 0.875rem 1.5rem;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(255, 80, 163, 0.35);
  transition: transform 0.15s, box-shadow 0.15s;
}

.btn-primary-pink:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(255, 80, 163, 0.45);
}

.btn-primary-pink:active {
  transform: translateY(0);
}

.btn-share-whatsapp {
  width: 100%;
  background: #fff;
  color: #25D366;
  border: 1.5px solid #25D366;
  border-radius: 0.85rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
}

.btn-share-whatsapp:hover {
  background: #25D366;
  color: #fff;
}

.btn-ghost {
  width: 100%;
  background: transparent;
  color: #6b7280;
  border: 1.5px solid #e5e7eb;
  border-radius: 0.85rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, color 0.2s;
}

.btn-ghost:hover {
  border-color: #9ca3af;
  color: #374151;
}

.btn-customize {
  color: #7c3aed;
  border-color: #ddd6fe;
}

.btn-customize:hover {
  border-color: #a78bfa;
  color: #5b21b6;
}

.success-inline-actions {
  display: flex;
  gap: 0.75rem;
}

.btn-inline {
  flex: 1;
  font-size: 0.82rem;
  padding: 0.55rem 0.75rem;
}

.onboarding-note {
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 0.85rem;
  padding: 0.7rem 0.85rem;
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
}

.onboarding-note__icon {
  color: #f97316;
  margin-top: 2px;
}

.onboarding-note__text {
  color: #9a3412;
  font-size: 0.87rem;
  line-height: 1.45;
}

.onboarding-session-hint {
  font-size: 0.82rem;
  color: #6b7280;
}

.onboarding-actions {
  display: flex;
  gap: 0.6rem;
}

@media (max-width: 767.98px) {
  .success-icon-circle.warning {
    margin-bottom: 1rem;
  }

  .success-body--activation {
    margin-bottom: 1rem;
    max-width: 100%;
  }

  .success-headline--activation {
    font-size: 20px;
    line-height: 1.3;
    margin-bottom: 0.35rem;
  }

  .success-sub--activation {
    font-size: 15px;
    line-height: 1.5;
  }

  .onboarding-actions {
    flex-direction: column;
  }
}

.confetti-bg {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
}

.confetti-piece {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  opacity: 0;
  animation: confetti-fall 3.5s ease-in forwards;
}

.confetti-1  { left: 5%;  background: #ff50a3; animation-delay: 0.0s; animation-duration: 3.2s; transform: rotate(20deg); }
.confetti-2  { left: 12%; background: #fbbf24; animation-delay: 0.2s; animation-duration: 3.8s; width: 6px; height: 10px; }
.confetti-3  { left: 20%; background: #60a5fa; animation-delay: 0.1s; animation-duration: 3.0s; border-radius: 50%; }
.confetti-4  { left: 30%; background: #ff50a3; animation-delay: 0.4s; animation-duration: 3.5s; }
.confetti-5  { left: 38%; background: #a78bfa; animation-delay: 0.0s; animation-duration: 4.0s; width: 5px; height: 12px; }
.confetti-6  { left: 47%; background: #34d399; animation-delay: 0.3s; animation-duration: 3.3s; }
.confetti-7  { left: 55%; background: #fbbf24; animation-delay: 0.5s; animation-duration: 3.7s; border-radius: 50%; }
.confetti-8  { left: 63%; background: #ff50a3; animation-delay: 0.1s; animation-duration: 3.1s; }
.confetti-9  { left: 72%; background: #60a5fa; animation-delay: 0.6s; animation-duration: 3.9s; width: 10px; height: 6px; }
.confetti-10 { left: 80%; background: #a78bfa; animation-delay: 0.2s; animation-duration: 3.4s; }
.confetti-11 { left: 88%; background: #34d399; animation-delay: 0.4s; animation-duration: 3.6s; border-radius: 50%; }
.confetti-12 { left: 95%; background: #fbbf24; animation-delay: 0.0s; animation-duration: 3.2s; }
.confetti-13 { left: 8%;  background: #a78bfa; animation-delay: 0.7s; animation-duration: 4.1s; width: 6px; height: 10px; }
.confetti-14 { left: 25%; background: #34d399; animation-delay: 0.3s; animation-duration: 3.5s; transform: rotate(-15deg); }
.confetti-15 { left: 43%; background: #fbbf24; animation-delay: 0.8s; animation-duration: 3.8s; border-radius: 50%; }
.confetti-16 { left: 60%; background: #ff50a3; animation-delay: 0.5s; animation-duration: 3.0s; width: 12px; height: 5px; }
.confetti-17 { left: 75%; background: #60a5fa; animation-delay: 0.2s; animation-duration: 3.6s; }
.confetti-18 { left: 92%; background: #ff50a3; animation-delay: 0.9s; animation-duration: 4.0s; transform: rotate(45deg); }

@keyframes confetti-fall {
  0%   { opacity: 1; top: -10px; transform: translateX(0) rotate(0deg); }
  100% { opacity: 0; top: 110%;  transform: translateX(40px) rotate(360deg); }
}
</style>
